import React from "react";

import { computed, observable } from "mobx";
import { observer } from "mobx-react";
import {
  withRouter,
  RouteComponentProps,
  generatePath,
} from "react-router-dom";
import { FormSpy } from "react-final-form";
import { debounce } from "debounce";
import KeyboardBackspaceIcon from "@material-ui/icons/KeyboardBackspace";
import { Button } from "@material-ui/core";

import Page from "components/Layout/Page";
import LoadingButton from "components/LoadingButton";
import FormControl, { TextFieldControl } from "components/FormControl";
import SubmitButtonControl, {
  CancelButtonControl,
} from "components/FormControl/SubmitButtonControl";
import Popup from "components/Popup";
import ConfirmationDialog from "components/ConfirmationDialog";
import PublishStatus from "components/PublishStatus";
import SimpleField from "components/SimpleField";
import LayoutContainer from "components/Layout/Container";
import Header from "components/Layout/Header";
import PublishButton from "components/PublishButton";
import { SNACKBAR_TYPES } from "components/Snackbar";
import { imageTypesValidator, lengthValidator } from "utils/validators";
import { PUBLISH_STATUSES } from "constants/models";
import ROUTES from "navigation/routes";
import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";
import {
  BackButtonWrapper,
  Container,
  ControlsContainer,
  ControlContainer,
  Column,
  GroupRow,
  Row,
  PublishStatusWrapper,
} from "./styles";
import HealthConditionStore, {
  HealthConditionDetail,
} from "stores/HealthConditionStore";
import { constants } from "../constants";

type LocationState = {
  editMode: boolean;
  readOnly?: boolean;
};

type Props = RouteComponentProps<{ id: string }, {}, LocationState> &
  SnackbarContextProps & {};

@observer
class HealthStoreDetails extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;
  @observable closeConfirmationIsOpened: boolean = false;
  @observable updateConfirmationIsOpened: boolean = false;
  @observable onAccept: () => void = () => {};

  quillEditorRef = React.createRef();

  get locationState() {
    return this.props.location.state;
  }

  get isEditMode() {
    return this.locationState?.editMode;
  }

  get isReadOnlyMode() {
    return this.locationState?.readOnly;
  }

  get isNewItem() {
    const { id } = this.props.match.params;

    return id === undefined;
  }

  get itemId() {
    const { id } = this.props.match.params;

    const parsedId = Number.parseInt(id);
    return typeof parsedId === "number" ? parsedId : undefined;
  }

  get backClickInfo() {
    return constants.healthConditionDetails.backButton;
  }

  get backClickPath() {
    return ROUTES.CONTENT_HEALTH_CONDITION;
  }

  @computed get displayValues(): any {
    const formData = HealthConditionStore.details?.formData;

    return {
      ...formData,
    };
  }

  @computed get initialValues() {
    if (this.isNewItem) {
      return {};
    } else {
      return {
        ...HealthConditionStore.details?.formData,
      };
    }
  }

  @computed get publishStatus() {
    return HealthConditionStore.details?.publishStatus;
  }

  async componentDidMount() {
    const { history } = this.props;

    if (this.isNewItem) {
      history.replace({ state: { editMode: true } });
    } else {
      await HealthConditionStore.fetchDetails(this.itemId);
    }
  }

  componentWillUnmount() {
    HealthConditionStore.resetDetails();
  }

  formFileValidator = (acceptedTypes: string) => (value?: File) =>
    imageTypesValidator(value?.type ?? "", acceptedTypes);

  closeConfirmationPopup = () => {
    this.closeConfirmationIsOpened = false;
  };

  onCloseForm = (onAccept: () => void) => {
    if (this.isPopupDirty) {
      this.closeConfirmationIsOpened = true;
      this.onAccept = onAccept;
    } else {
      onAccept();
    }
  };

  onBackClick = () => {
    const onAccept = () => this.props.history.push(this.backClickPath);

    if (this.isEditMode) {
      this.onCloseForm(onAccept);
    } else {
      onAccept();
    }
  };

  onCancelClick = (resetForm: () => void) => {
    if (this.isNewItem) {
      this.onBackClick();
    } else {
      const onAccept = () => {
        this.exitEditMode();
        this.closeConfirmationPopup();
        this.resetIsPopupDirty();
        resetForm();
      };
      this.onCloseForm(onAccept);
    }
  };

  onEditClick = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_HEALTH_CONDITION_DETAILS, {
      id: this.itemId,
    });
    history.replace({ pathname: path, state: { editMode: true } });
  };

  exitEditMode = () => {
    const { history } = this.props;

    const path = generatePath(ROUTES.CONTENT_HEALTH_CONDITION_DETAILS, {
      id: this.itemId,
    });
    history.replace({ pathname: path, state: { editMode: false } });
  };

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  openUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = true;
  };

  closeUpdateConfirmation = () => {
    this.updateConfirmationIsOpened = false;
  };

  confirmUpdate = (values: HealthConditionDetail) => () => {
    this.closeUpdateConfirmation();
    this.submit(values);
  };

  onSubmit = (values: any) => {
    const shouldShowUpdateConfirmation =
      this.publishStatus &&
      [
        PUBLISH_STATUSES.PUBLISHED,
        PUBLISH_STATUSES.SCHEDULE_FOR_REMOVE,
      ].includes(this.publishStatus);

    shouldShowUpdateConfirmation
      ? this.openUpdateConfirmation()
      : this.submit(values);
  };

  submit = async (values: HealthConditionDetail) => {
    const { setMessage, history } = this.props;
    const { id, title, shortDescription, description } = values;

    const data = {
      title: title?.trim(),
      shortDescription: shortDescription?.trim(),
      description: description?.trim(),
    };

    try {
      if (this.isNewItem) {
        const payload = { ...data };
        const id = await HealthConditionStore.createItem(payload);

        if (id) {
          const path = generatePath(ROUTES.CONTENT_HEALTH_CONDITION_DETAILS, {
            id,
          });
          history.push({ pathname: path, state: { editMode: false } });
          HealthConditionStore.fetchDetails(id);
        }
      } else {
        const payload = { ...data, id };
        await HealthConditionStore.editItem(payload);

        this.exitEditMode();
        HealthConditionStore.fetchDetails(id);
      }
      this.resetIsPopupDirty();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <LayoutContainer header={<Header controls={<PublishButton />} />}>
        <Page
          title={constants.healthConditionDetails.title}
          leftControls={
            this.publishStatus && (
              <PublishStatusWrapper>
                <PublishStatus status={this.publishStatus} />
              </PublishStatusWrapper>
            )
          }
          topControls={
            <BackButtonWrapper>
              <Button
                color="secondary"
                variant="text"
                startIcon={<KeyboardBackspaceIcon />}
                onClick={this.onBackClick}
              >
                {this.backClickInfo}
              </Button>
            </BackButtonWrapper>
          }
          isLoading={HealthConditionStore.isLoading}
        >
          {this.closeConfirmationIsOpened && (
            <Popup onClose={this.closeConfirmationPopup} withoutExitButton>
              <ConfirmationDialog
                title="Discard Changes"
                text="Are you sure you would like to discard changes? All information will be lost."
                declineText="Cancel"
                acceptText="Discard"
                onDecline={this.closeConfirmationPopup}
                onAccept={this.onAccept}
              />
            </Popup>
          )}
          <FormControl
            onSubmit={this.onSubmit}
            initialValues={this.initialValues}
            keepDirtyOnReinitialize={this.isNewItem}
          >
            {this.updateConfirmationIsOpened && (
              <FormSpy subscription={{ values: true }}>
                {({ values }: { values: HealthConditionDetail }) => (
                  <Popup
                    onClose={this.closeUpdateConfirmation}
                    withoutExitButton
                  >
                    <ConfirmationDialog
                      title="Save Changes to Production"
                      text="All changes made will be published to production. Are you sure you want to proceed?"
                      acceptText="Proceed"
                      declineText="Cancel"
                      onAccept={this.confirmUpdate(values)}
                      onDecline={this.closeUpdateConfirmation}
                    />
                  </Popup>
                )}
              </FormSpy>
            )}
            <FormSpy
              subscription={{
                dirty: true,
                dirtyFields: true,
                initialValues: true,
                values: true,
              }}
              onChange={debounce(this.onChangeIsPopupDirty, 500)}
            />
            <Container>
              <ControlsContainer>
                {this.isEditMode ? (
                  <>
                    <ControlContainer>
                      <CancelButtonControl
                        render={this.renderCancelButton}
                        onClick={this.onCancelClick}
                      />
                    </ControlContainer>
                    <ControlContainer>
                      <SubmitButtonControl render={this.renderButton} />
                    </ControlContainer>
                  </>
                ) : (
                  !this.isReadOnlyMode && (
                    <ControlContainer>
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={this.onEditClick}
                        fullWidth
                      >
                        Edit
                      </Button>
                    </ControlContainer>
                  )
                )}
              </ControlsContainer>

              <GroupRow>
                <Column>
                  <Row>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="title"
                        label="Title"
                        validate={lengthValidator(100)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Title"
                        value={this.displayValues?.title}
                      />
                    )}
                  </Row>
                  <Row>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="shortDescription"
                        label="Short Description"
                        validate={lengthValidator(128)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Short Description"
                        value={this.displayValues?.shortDescription}
                      />
                    )}
                  </Row>
                  <Row>
                    {this.isEditMode ? (
                      <TextFieldControl
                        variant="outlined"
                        margin="none"
                        name="description"
                        label="Description"
                        rows={5}
                        rowsMax={10}
                        multiline
                        validate={lengthValidator(1024)}
                        fullWidth
                        required
                      />
                    ) : (
                      <SimpleField
                        label="Description"
                        value={this.displayValues?.description}
                      />
                    )}
                  </Row>
                </Column>
              </GroupRow>
            </Container>
          </FormControl>
        </Page>
      </LayoutContainer>
    );
  }

  renderButton = ({ onClick }: { onClick: () => void }) => (
    <LoadingButton
      onClick={onClick}
      variant="contained"
      color="primary"
      isLoading={HealthConditionStore.isManaging}
      fullWidth
    >
      {this.isNewItem ? "Add" : "Save"}
    </LoadingButton>
  );

  renderCancelButton = ({ onClick }: { onClick: () => void }) => (
    <Button variant="outlined" color="primary" onClick={onClick} fullWidth>
      Cancel
    </Button>
  );
}

export default withGlobalSnackbar(withRouter(HealthStoreDetails));
